// Mehmon portalining ko'rinishi — `xsib-venue` `PortalTemplateResource` bilan
// haqiqiy kontrakt (`GET`/`PUT /api/venues/{venueId}/portal-template`).
//
// ⚠️ IKKI HUQUQ, IKKI DARAJA (backend javadoc'i):
//   · o'qish — `venue.read` (ko'rinish maxfiy emas, uni har mehmon ko'radi)
//   · yozish — `portal.template.update`, `venue.update` EMAS. Menejerga
//     brendni o'zgartirishga ruxsat berish uning SAQLASH MUDDATLARINI ham
//     o'zgartira olishini anglatmasligi kerak.
//
// ⚠️ BU YERDA IDENTIFIKATSIYA USULI YO'Q va bo'lmaydi. `allowedMethods` /
// `minStrength` zona sozlamasida qoladi (`VenueZonesPanel`) — bu HUQUQIY
// qaror: aks holda "rang tanlash" huquqi identifikatsiyani o'chirish
// huquqiga aylanardi (№137 qarori, `PortalTemplate.java` javadoc).

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'

const portalTemplateSchema = z.object({
  venueId: uuidSchema,
  logoUrl: z.string().nullish(),
  primaryColor: z.string().nullish(),
  welcomeUz: z.string().nullish(),
  welcomeRu: z.string().nullish(),
  welcomeEn: z.string().nullish(),
  termsUrl: z.string().nullish(),
  supportPhone: z.string().nullish(),
  updatedAt: instantSchema.nullish(),
  updatedBy: z.string().nullish(),
  /** Backend hisoblab beradi — panelda qayta hisoblanmaydi */
  configured: z.boolean().nullish(),
})

export interface PortalTemplate {
  venueId: string
  logoUrl: string | null
  primaryColor: string | null
  welcomeUz: string | null
  welcomeRu: string | null
  welcomeEn: string | null
  termsUrl: string | null
  supportPhone: string | null
  updatedAt: string | null
  updatedBy: string | null
  configured: boolean
}

function toTemplate(raw: z.infer<typeof portalTemplateSchema>): PortalTemplate {
  return {
    venueId: raw.venueId,
    logoUrl: raw.logoUrl ?? null,
    primaryColor: raw.primaryColor ?? null,
    welcomeUz: raw.welcomeUz ?? null,
    welcomeRu: raw.welcomeRu ?? null,
    welcomeEn: raw.welcomeEn ?? null,
    termsUrl: raw.termsUrl ?? null,
    supportPhone: raw.supportPhone ?? null,
    updatedAt: raw.updatedAt ?? null,
    updatedBy: raw.updatedBy ?? null,
    configured: raw.configured ?? false,
  }
}

/**
 * ⚠️ Sozlanmagan ob'ekt uchun `404` EMAS, BO'SH shablon qaytadi
 * (backend `PortalTemplateResource#get` javadoc'i). Ya'ni bu chaqiruv
 * "ob'ekt yo'qmi yoki shablon yo'qmi?" degan savolni umuman tug'dirmaydi.
 */
export async function fetchPortalTemplate(venueId: string): Promise<PortalTemplate> {
  const response = await api.get<unknown>(`/api/venues/${venueId}/portal-template`)
  return toTemplate(portalTemplateSchema.parse(response.data))
}

/**
 * ⚠️ BO'SH QIYMAT = TOZALASH, "tegilmasin" emas (`PortalTemplateDraft`
 * javadoc'i). Shuning uchun bu yerda har doim TO'LIQ shakl yuboriladi:
 * qisman yuborilsa, tushirib qoldirilgan maydon jimgina o'chib ketardi.
 */
export interface PortalTemplateDraft {
  logoUrl: string | null
  primaryColor: string | null
  welcomeUz: string | null
  welcomeRu: string | null
  welcomeEn: string | null
  termsUrl: string | null
  supportPhone: string | null
}

export async function updatePortalTemplate(
  venueId: string,
  draft: PortalTemplateDraft,
): Promise<PortalTemplate> {
  const response = await api.put<unknown>(`/api/venues/${venueId}/portal-template`, draft)
  return toTemplate(portalTemplateSchema.parse(response.data))
}

/**
 * Panel tomonidagi BIRINCHI qatlam tekshiruvlari — backendning aynan
 * o'sha qoidalari (`PortalTemplateDraft`).
 *
 * ⚠️ Bu backend tekshiruvini ALMASHTIRMAYDI, faqat operatorga xatoni
 * saqlashdan OLDIN ko'rsatadi. Server baribir qayta tekshiradi.
 */
export const PORTAL_TEMPLATE_RULES = {
  /** Faqat `https://` — `javascript:`/`data:` mehmon brauzerida bajarilardi */
  httpsUrl: /^https:\/\/[^\s"<>]+$/i,
  /** `#RRGGBB` — backend qisqa `#RGB` shaklini QABUL QILMAYDI */
  hexColor: /^#[0-9a-fA-F]{6}$/,
  /** `<` va `>` umuman taqiqlangan: soxta forma qo'yish yo'li */
  noMarkup: /^[^<>]*$/,
  welcomeMax: 500,
  phoneMax: 64,
  urlMax: 2000,
} as const
